<script lang="ts" setup>
import Notifications from './core/components/Notifications.vue';

import { useKeycloak } from '@nxtpeople/nxt-vue-keycloak';
import { logEventWithPosthog, useMediaQueries } from '@nxtpeople/nxt-styles';
import Banner from './core/components/Banner.vue';
import GlobalModals from './core/components/GlobalModals.vue';
import MobileNavigation from './core/components/layout-elements/MobileNavigation.vue';
import PageHeader from './core/components/layout-elements/PageHeader.vue';
import Mainbar from './core/components/Mainbar.vue';
import { PosthogEvent } from './core/enums/posthog.enum';
import SettingsPanel from './modules/settings/components/SettingsPanel.vue';

const route = useRoute();
const router = useRouter();
const { state, commit } = useStore();
const { md, sm, xs } = useMediaQueries();
const { keycloak } = useKeycloak();
const { t } = useI18n();

onMounted(() => {
   if (!md.value) return;

   const LAST_ROUTE = localStorage.getItem('lastRoute');
   if (!LAST_ROUTE) return;
   router.push(LAST_ROUTE);
});

//#region Authentication
const { loading } = useApp();
const { isAuthenticated, isPending } = useKeycloak();
//#endregion

//#region Settings
const showSettingsPanel = ref<boolean>(false);
//#endregion

//#region Variables
const placesApiKey = import.meta.env.VITE_APP_PLACES_API_KEY;
const placesIndex = import.meta.env.VITE_APP_PLACES_INDEX;
//#endregion

// #region logout
function handleLogout() {
   keycloak.logout({ redirectUri: window.location.origin });
}
// #endregion

// #region posthog
const isProduction = computed(() => import.meta.env.VITE_APP_ENV === 'prd');
const API_KEY = import.meta.env.VITE_APP_POSTHOG_API_KEY;
const API_HOST = import.meta.env.VITE_APP_POSTHOG_API_HOST;
// #endregion

onMounted(() => {
   logEventWithPosthog(PosthogEvent.NxtPlanTrackAppMount);
});

// #regino route
watch(
   () => route.name,
   (routeName) => {
      if (routeName === 'CandidatesOverview') {
         commit('header/SET_BREADCRUMBS', [{ to: '/candidates', content: t('nxt-candidates.talent') }]);
      }
   },
);
// #endregion

// #region is mobile
const isMobile = computed(() => md.value || sm.value || xs.value);
provide('isMobile', isMobile);
// #endregion
</script>

<template>
   <div class="app" :class="{ '-md': md, '-sm': sm, '-xs': xs }">
      <template v-if="!state.user?.has_no_user_info">
         <NxtMainLayout :user-info="state.user?.user_info" :http="api" :router="router" :env-variables="{ placesApiKey, placesIndex }">
            <template #header>
               <PageHeader @on-show-settings-panel="showSettingsPanel = true" />
            </template>

            <template #nav>
               <Mainbar />
            </template>

            <template #mobileNav>
               <MobileNavigation />
            </template>

            <template v-if="isAuthenticated && !loading" #status-banner>
               <Banner />
            </template>

            <RouterView v-if="isAuthenticated && !loading" v-slot="{ Component }">
               <transition name="fade" mode="out-in">
                  <Component :is="Component" />
               </transition>
            </RouterView>

            <!-- Modals -->
            <GlobalModals />

            <!-- Settings -->
            <Transition name="fade">
               <SettingsPanel v-if="showSettingsPanel && !isPending" @on-close-settings="showSettingsPanel = false" />
            </Transition>
         </NxtMainLayout>

         <!-- POSTHOG -->
         <NxtPosthog v-if="isProduction" :api-key="API_KEY" :api-host="API_HOST" :router="router" :user-info="state.user?.user_info" />

         <!-- Notifications -->
         <Notifications />
      </template>

      <template v-else>
         <NxtErrorPage
            :title="$t('global.no_access')"
            :message="$t('global.no_access_message', { email: 'info@nxtpeople.com' })"
            :logout-action-text="$t('actions.sign_out')"
            :show-home-action="false"
            @on-logout="handleLogout()"
         />
      </template>
   </div>
</template>

<style lang="scss" scoped>
.app :deep(.nxt-main-layout__page__base) {
   padding-bottom: unset;
}

.app :deep(.nxt-vertical-navbar) {
   height: 100%;
}

:deep(.nxt-main-layout__page__base):has(.board-details) {
   padding: 0;
}

:deep(.nxt-main-layout__page__header) {
   padding-top: env(safe-area-inset-top);
}
</style>
